import { formatBytes } from '../lib/format'
import type { Quota, Readiness } from '../lib/api'
import { ProgressBar, Spinner } from './ui'

interface Props {
  readiness: Readiness
  quota: Quota
  isReady: boolean
  busy: boolean
  disabled: boolean
  confirmationNote: string | null
  onReady: () => void
  onUnready: () => void
}

/**
 * The sticky submit bar. It doubles as the completeness meter: the recipient can
 * always see how many required sections are still open and exactly how much room
 * is left, so pressing Ready? is never a guess.
 */
export function ReadyBar({
  readiness,
  quota,
  isReady,
  busy,
  disabled,
  confirmationNote,
  onReady,
  onUnready,
}: Props) {
  const { requiredFilled, requiredTotal, ready } = readiness
  const usedRatio = quota.maxBytes === 0 ? 0 : quota.usedBytes / quota.maxBytes

  return (
    <div className="sticky bottom-0 z-20 -mx-4 mt-8 border-t border-zinc-200 bg-white/85 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6 dark:border-zinc-800 dark:bg-zinc-950/85">
      <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          {isReady ? (
            <div>
              <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                Submitted — thank you
              </div>
              <div className="text-xs text-zinc-500">
                {confirmationNote ??
                  'The owner will be notified shortly. You can still make changes until then.'}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-sm font-semibold">
                {requiredTotal === 0
                  ? 'Nothing is required — submit whenever you like'
                  : `${requiredFilled} of ${requiredTotal} required sections complete`}
              </div>
              <div className="mt-1.5 max-w-xs">
                <ProgressBar
                  value={usedRatio}
                  tone={usedRatio > 0.9 ? 'danger' : 'brand'}
                />
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {formatBytes(quota.usedBytes)} of {formatBytes(quota.maxBytes)} used
              </div>
            </div>
          )}
        </div>

        {isReady ? (
          <button type="button" onClick={onUnready} disabled={busy || disabled} className="btn-ghost">
            {busy && <Spinner />}
            Unready — I want to change something
          </button>
        ) : (
          <button
            type="button"
            onClick={onReady}
            disabled={!ready || busy || disabled}
            title={ready ? undefined : 'Fill in every required section first'}
            className="btn-primary"
          >
            {busy && <Spinner />}
            Ready?
          </button>
        )}
      </div>
    </div>
  )
}
